import React, { useCallback, useEffect, useState } from 'react';
import { Link2, AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import API_URL from '@/config/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import LinkToXeroButton from '@/components/pricing/LinkToXeroButton';

const SRC = { installer_orders: 'Installer orders', prodecor_orders: 'ProDecor orders', proepoxy_orders: 'ProEpoxy orders', order_builder: 'ProEpoxy order builder', quote_tool: 'Quote tool', customer_quotes: 'Customer quotes' };
const money = (n) => `£${Number(n || 0).toFixed(2)}`;

/** Products that actually appear on material orders / quotes — are they linked to Xero and do they have a cost? */
export function XeroLinkStatusCard({ reloadKey, onChanged, onLoaded }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('unlinked');

  const load = useCallback(async () => {
    const res = await fetch(`${API_URL}/api/staff/product-pricing/xero-link-status`, { credentials: 'include' });
    if (res.ok) { const d = await res.json(); setData(d); onLoaded?.(d); }
  }, [onLoaded]);
  useEffect(() => { load(); }, [load, reloadKey]);

  if (!data) return null;
  const rows = data.products.filter((p) => (filter === 'unlinked' ? !p.xero_linked : filter === 'nocost' ? p.unit_cost == null : true));
  const allGood = data.unlinked === 0 && data.no_cost === 0;

  return (
    <Card className={`p-4 ${allGood ? 'border-emerald-200' : 'border-amber-300 bg-amber-50/40'}`} data-testid="xero-link-status">
      <div className="flex flex-wrap items-center gap-3">
        {allGood ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <AlertTriangle className="w-5 h-5 text-amber-600" />}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-gray-900">Products used on material orders — Xero link &amp; cost</div>
          <div className="text-xs text-gray-600" data-testid="xero-link-status-summary">
            {data.used} product{data.used === 1 ? '' : 's'} in use · <span className={data.unlinked ? 'text-amber-800 font-semibold' : ''}>{data.unlinked} not linked to Xero</span> · <span className={data.no_cost ? 'text-amber-800 font-semibold' : ''}>{data.no_cost} without a cost</span>
            {!data.xero_connected && <span className="ml-2 text-rose-700 font-semibold">· Xero not connected</span>}
          </div>
        </div>
        <button type="button" onClick={load} className="p-1.5 text-gray-400 hover:text-gray-700" aria-label="Refresh" data-testid="xero-link-status-refresh"><RefreshCw className="w-4 h-4" /></button>
        <Button size="sm" variant="outline" onClick={() => setOpen((o) => !o)} data-testid="xero-link-status-toggle">{open ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}{open ? 'Hide' : 'Show list'}</Button>
      </div>

      {open && (
        <div className="mt-3 space-y-2">
          <div className="flex gap-1.5 text-xs">
            {[['unlinked', `Not linked (${data.unlinked})`], ['nocost', `No cost (${data.no_cost})`], ['all', `All in use (${data.used})`]].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setFilter(k)} className={`px-2.5 py-1 rounded-full border font-semibold ${filter === k ? 'bg-[#0B0B0E] text-white border-transparent' : 'bg-white text-gray-700 border-gray-200'}`} data-testid={`xero-link-filter-${k}`}>{l}</button>
            ))}
          </div>
          {rows.length === 0 ? <div className="text-sm text-gray-500 py-3">Nothing here — all sorted.</div> : (
            <div className="bg-white border rounded-lg divide-y max-h-[420px] overflow-y-auto" data-testid="xero-link-status-list">
              {rows.map((p) => (
                <div key={p.id} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`xero-status-row-${p.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-900 truncate">{p.name}</div>
                    <div className="text-[11px] text-gray-500 truncate">{p.category}{p.sku ? ` · SKU ${p.sku}` : ''} · used in {p.used_in.map((u) => SRC[u] || u).join(', ')}</div>
                  </div>
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${p.unit_cost != null ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>{p.unit_cost != null ? `cost ${money(p.unit_cost)}` : 'no cost'}</span>
                  {p.xero_linked ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-sky-700"><Link2 className="w-3 h-3" /> {p.xero_code || 'linked'}</span> : <LinkToXeroButton product={p} onChanged={() => { load(); onChanged?.(); }} />}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
